import { Link } from 'react-router-dom';
import { CheckCircle2, ClipboardList, ExternalLink, Play, Radio, type LucideIcon } from 'lucide-react';
import { useStore } from '../hooks/useStore';
import { useActiveTournament } from '../state/TournamentContext';
import { getActiveScoringSession, getLiveScore, getResultForGame } from '../services/scoringService';
import { GuidedEmpty } from '../components/GuidedEmpty';
import { EventWorkspaceSection } from '../components/PageTemplates';

/**
 * Event scoring queue. Seasons and tournaments both land here before opening
 * the shared scoresheet console, so game-day operations stay one workflow.
 */
export function TournamentScoringPage() {
  const { tournament, games, teams } = useActiveTournament();
  const db = useStore((d) => d);

  const slotById = new Map(db.scheduleSlots.map((slot) => [slot.id, slot]));
  const teamById = new Map(teams.map((team) => [team.id, team]));
  const profileById = new Map(db.profiles.map((profile) => [profile.id, profile]));
  const startOf = (gameId: string) => {
    const game = games.find((row) => row.id === gameId);
    if (!game?.scheduleSlotId) return '';
    return slotById.get(game.scheduleSlotId)?.startsAt ?? '';
  };
  const sorted = [...games].sort((a, b) => startOf(a.id).localeCompare(startOf(b.id)));
  const summary = {
    ready: games.filter((game) => game.status === 'scheduled' || game.status === 'ready').length,
    live: games.filter((game) => game.status === 'in_progress').length,
    final: games.filter((game) => game.status === 'final' || game.status === 'forfeit').length,
  };

  if (games.length === 0) {
    return (
      <GuidedEmpty
        icon={ClipboardList}
        title="No games ready to score"
        body="The scoring queue is created from the event schedule. Build or edit the schedule first, then each game will open the same scorer-table console."
        action={{ to: '../schedule', label: 'Open schedule' }}
      />
    );
  }

  return (
    <EventWorkspaceSection>
      <div className="grid grid--3">
        <QueueMetric icon={ClipboardList} label="Ready" value={summary.ready} />
        <QueueMetric icon={Radio} label="Live" value={summary.live} />
        <QueueMetric icon={CheckCircle2} label="Final" value={summary.final} />
      </div>

      <div className="card">
        <div className="card__head">
          <div>
            <h3 className="card__title">Scoring queue</h3>
            <p className="card__sub">
              Open any {tournament.format === 'season' ? 'season' : 'tournament'} game in the shared scoring console.
            </p>
          </div>
        </div>
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Start</th>
                <th>Match</th>
                <th>Status</th>
                <th>Score</th>
                <th>Crew</th>
                <th>Session</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((game) => {
                const home = teamById.get(game.homeTeamId);
                const away = teamById.get(game.awayTeamId);
                const result = getResultForGame(game.id);
                const live = game.status === 'in_progress' ? getLiveScore(game.id) : null;
                const start = startOf(game.id);
                const activeSession = getActiveScoringSession(game.id);
                const crew = db.gameAssignments
                  .filter(
                    (assignment) =>
                      assignment.gameId === game.id &&
                      assignment.status !== 'declined' &&
                      (assignment.position === 'scorekeeper' || assignment.position === 'timekeeper'),
                  )
                  .map((assignment) => profileById.get(assignment.profileId)?.displayName ?? assignment.position);
                return (
                  <tr key={game.id}>
                    <td className="muted">{start ? new Date(start).toLocaleString() : '-'}</td>
                    <td>
                      <span style={{ fontWeight: 700 }}>{home?.name ?? '?'}</span>
                      {' vs '}
                      <span style={{ fontWeight: 700 }}>{away?.name ?? '?'}</span>
                      {game.displayLabel && <div className="muted" style={{ fontSize: 12 }}>{game.displayLabel}</div>}
                    </td>
                    <td>
                      <span className={`chip chip--${game.status === 'in_progress' ? 'ok' : game.status === 'final' || game.status === 'forfeit' ? 'info' : 'warn'}`}>
                        <span className="chip__dot" />
                        {game.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="muted">
                      {result ? `${result.homeScore} - ${result.awayScore}` : live ? `${live.home} - ${live.away}` : '-'}
                    </td>
                    <td className="muted">{crew.length > 0 ? crew.join(', ') : 'Unassigned'}</td>
                    <td className="muted">
                      {activeSession
                        ? `${profileById.get(activeSession.profileId)?.displayName ?? 'Scorer'} active`
                        : game.status === 'final' || game.status === 'forfeit'
                          ? 'Closed'
                          : 'Open'}
                    </td>
                    <td>
                      <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
                        <Link to={`/events/${tournament.slug}/games/${game.id}`} className="btn btn--ghost btn--sm">
                          <ExternalLink size={12} /> Public
                        </Link>
                        <Link to={`/admin/scoring/${game.id}`} className="btn btn--ghost btn--sm">
                          <Play size={12} /> Score
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </EventWorkspaceSection>
  );
}

function QueueMetric({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: number }) {
  return (
    <div className="card card--glass">
      <div className="row" style={{ gap: 10 }}>
        <Icon size={18} color="var(--brand-arc)" />
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{value}</div>
          <div className="eyebrow" style={{ marginTop: 4 }}>{label}</div>
        </div>
      </div>
    </div>
  );
}
